import Alert from "@cloudscape-design/components/alert"
import Button, { type ButtonProps } from "@cloudscape-design/components/button"
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Toast, useNotifications } from "../../contexts/NotificationContext"
import "./NotificationToast.css"

/**
 * Map a toast variant to the Cloudscape Alert type.
 */
function alertType(variant: Toast["variant"]): "success" | "error" | "warning" | "info" {
  if (variant === "success") return "success"
  if (variant === "error") return "error"
  if (variant === "warning") return "warning"
  return "info"
}

const ToastItem = ({
  toast,
  onDismiss,
  interactive = true,
}: {
  toast: Toast
  onDismiss: () => void
  interactive?: boolean
}) => {
  const navigate = useNavigate()
  const [dismissing, setDismissing] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const animateDismiss = (then?: () => void) => {
    setDismissing(true)
    timerRef.current = setTimeout(() => {
      onDismiss()
      then?.()
    }, 200)
  }

  const handleClick = () => {
    if (!interactive) return
    toast.onClick?.()
    const target = toast.actions?.[0]
    animateDismiss(() => {
      if (target) navigate(target.to)
    })
  }

  const handleActionClick = (e: CustomEvent<ButtonProps.ClickDetail>, to: string) => {
    e.stopPropagation()
    toast.onClick?.()
    animateDismiss(() => navigate(to))
  }

  // Nav links render in the Alert's action slot.
  const actionSlot =
    toast.actions && toast.actions.length > 0 ? (
      <div className="toast-actions">
        {toast.actions.map((action, index) => (
          <Button
            key={index}
            variant="inline-link"
            onClick={(e) => handleActionClick(e, action.to)}
          >
            {action.label}
          </Button>
        ))}
      </div>
    ) : undefined

  return (
    <div
      className={`notification-toast-item toast-${toast.variant}${dismissing ? " toast-dismissing" : ""}`}
      onClick={handleClick}
    >
      <Alert
        type={alertType(toast.variant)}
        header={toast.subject}
        action={actionSlot}
        dismissible
        dismissAriaLabel="Dismiss notification"
        onDismiss={() => onDismiss()}
      >
        {toast.message}
      </Alert>
    </div>
  )
}

const ToastContainer = () => {
  const { toasts, dismissToast, dismissAllToasts } = useNotifications()
  const [expanded, setExpanded] = useState(false)
  const [hovering, setHovering] = useState(false)
  const [dismissingAll, setDismissingAll] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const animateDismissAll = useCallback(() => {
    setDismissingAll(true)
    timerRef.current = setTimeout(() => {
      dismissAllToasts()
      setExpanded(false)
      setDismissingAll(false)
    }, 200)
  }, [dismissAllToasts])

  useEffect(() => {
    if (toasts.length === 0 || hovering) {
      if (timerRef.current) clearTimeout(timerRef.current)
      return
    }
    timerRef.current = setTimeout(animateDismissAll, 10000)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [toasts.length, hovering, animateDismissAll])

  if (toasts.length === 0) return null

  // Toasts are stored oldest-first (new ones are appended in the context). Render newest-first so
  // the most recent toast is the frontmost item in the stack: CSS gives nth-child(1) the top
  // z-index, no offset, and full opacity, and the container marks index 0 as interactive.
  const orderedToasts = [...toasts].reverse()

  return (
    <div
      className={`notification-toast-container${expanded ? " toast-expanded" : ""}${dismissingAll ? " toast-dismissing-all" : ""}`}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      role="log"
      aria-live="polite"
      aria-label="Notifications"
    >
      <div
        className="notification-toast-stack"
        onClick={(e) => {
          if (!expanded && toasts.length > 1) {
            const target = e.target as HTMLElement
            const firstItem = e.currentTarget.firstElementChild
            if (firstItem && !firstItem.contains(target)) {
              e.stopPropagation()
              setExpanded(true)
            }
          }
        }}
      >
        {orderedToasts.map((t, i) => (
          <ToastItem
            key={t.id}
            toast={t}
            onDismiss={() => {
              dismissToast(t.id)
              setExpanded(false)
            }}
            interactive={expanded || i === 0}
          />
        ))}
      </div>
    </div>
  )
}

export default ToastContainer
